import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { Mail, Sparkles, Sunrise, Sunset, Star } from "lucide-react";

/**
 * Iter 119 → 134g — Email digest opt-in panel for the account page.
 *
 * Four toggles (3 dedicated slip digests + 1 featured-picks digest):
 *   • Smart 5-Leg Slip    — 06:35 UTC, strict-edge accumulator
 *   • Morning Curated     — 06:30 UTC, 20-leg deep slip
 *   • Evening Bonus       — 16:30 UTC, tonight's 10-leg bonus
 *   • Featured Picks      — 07:00 UTC, picks + HT BTTS + weekend + elite + halftime
 *
 * Defaults: all 3 slip digests are ON for new users. Featured picks
 * remains explicit opt-in.
 *
 * Reads/writes through `/api/me/email-subscriptions`.
 */
const Toggle = ({ checked, onChange, disabled, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => onChange(!checked)}
    data-testid={testid}
    className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors disabled:opacity-50 ${
      checked ? "bg-emerald-500" : "bg-zinc-700"
    }`}
  >
    <span
      className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
        checked ? "translate-x-5" : "translate-x-0.5"
      }`}
    />
  </button>
);

const Row = ({ Icon, iconColor, title, desc, checked, onChange, saving, testid, first }) => (
  <div className={`flex items-start justify-between gap-4 ${first ? "" : "pt-4 border-t border-border/40"}`}>
    <div className="flex items-start gap-3 flex-1 min-w-0">
      <div className={`mt-0.5 h-8 w-8 rounded-lg bg-muted/60 flex items-center justify-center shrink-0 ${iconColor}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
      </div>
    </div>
    <Toggle checked={checked} onChange={onChange} disabled={saving} testid={testid} />
  </div>
);

export const EmailSubscriptions = () => {
  const [smart, setSmart] = useState(true);
  const [morning, setMorning] = useState(true);
  const [evening, setEvening] = useState(true);
  const [featured, setFeatured] = useState(false);
  const [email, setEmail] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/me/email-subscriptions`);
        if (cancelled) return;
        setSmart(!!data.smart_slip);
        setMorning(!!data.morning_curated);
        setEvening(!!data.evening_bonus);
        setFeatured(!!data.featured_picks);
        setEmail(data.email || "");
      } catch (_) {
        // Best-effort
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      await axios.patch(`${API}/me/email-subscriptions`, next);
      toast.success("Email preferences updated");
    } catch (e) {
      toast.error("Couldn't save email preferences");
    } finally {
      setSaving(false);
    }
  };

  if (!loaded) {
    return (
      <div className="rounded-lg border border-border/40 p-4 text-sm text-muted-foreground">
        Loading email preferences...
      </div>
    );
  }

  return (
    <section
      className="rounded-lg border border-border/40 p-5 space-y-4"
      data-testid="email-subscriptions-panel"
    >
      <div className="flex items-center gap-2">
        <Mail className="w-4 h-4 text-muted-foreground" />
        <Label className="eyebrow !text-foreground">Email digests</Label>
      </div>
      <p className="text-xs text-muted-foreground -mt-2">
        Delivered to <span className="font-mono">{email}</span>. Unsubscribe one-click from any email footer.
      </p>

      <Row
        first
        Icon={Sparkles}
        iconColor="text-emerald-500"
        title="Smart 5-Leg Slip"
        desc="Strict-edge picks (≥72% confidence + proven league-market hit-rate). 06:35 UTC."
        checked={smart}
        onChange={(v) => { setSmart(v); save({ smart_slip: v }); }}
        saving={saving}
        testid="email-sub-smart-slip"
      />

      <Row
        Icon={Sunrise}
        iconColor="text-amber-500"
        title="Morning Curated (20-leg)"
        desc="Algorithm-ranked deep slip across today's full slate. 06:30 UTC."
        checked={morning}
        onChange={(v) => { setMorning(v); save({ morning_curated: v }); }}
        saving={saving}
        testid="email-sub-morning-curated"
      />

      <Row
        Icon={Sunset}
        iconColor="text-violet-500"
        title="Evening Bonus"
        desc="Tonight's 10-leg bonus slip for late kickoffs. 16:30 UTC."
        checked={evening}
        onChange={(v) => { setEvening(v); save({ evening_bonus: v }); }}
        saving={saving}
        testid="email-sub-evening-bonus"
      />

      <Row
        Icon={Star}
        iconColor="text-sky-500"
        title="Featured Picks Digest"
        desc="Today's picks + HT BTTS Acca + Weekend Big Slate + Elite Leagues + Halftime, bundled. 07:00 UTC."
        checked={featured}
        onChange={(v) => { setFeatured(v); save({ featured_picks: v }); }}
        saving={saving}
        testid="email-sub-featured-picks"
      />
    </section>
  );
};

export default EmailSubscriptions;
